<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'

import { useApiTabStore } from '@/stores/apiTab.store'
import BaseInput from '@/components/forms/BaseInput.vue'
import BaseSelect from '@/components/forms/BaseSelect.vue'
import BaseTextbox from '@/components/forms/BaseTextbox.vue'
import { FORM_SELECT_VALUE_TYPE } from '@/components/forms/form.enum'
import { RequestSection } from '@/utils/apiTab'
import {
  AUTHORIZATION_AUTH_TYPES,
  JWT_AVAILABLE_ALGORITHMS,
  JWT_TOKEN_ADD_TO
} from '@/utils/apiTab'

const props = defineProps({
  activeRequestIndex: {
    type: Number,
    required: true
  }
})
const jwtTokenAddToFields = Object.values(JWT_TOKEN_ADD_TO)
const jwtAvailableAlgorithms = Object.values(JWT_AVAILABLE_ALGORITHMS)
const apiTabStore = useApiTabStore()

const jwtPayloads = ref<{
  jwtAddToRequest: string
  jwtAlgorithm: string
  jwtSecret: string
  jwtPayload: string
}>({
  jwtAddToRequest: JWT_TOKEN_ADD_TO.REQUEST_HEADERS,
  jwtAlgorithm: JWT_AVAILABLE_ALGORITHMS.HS256,
  jwtSecret: '',
  jwtPayload: ''
})
/**
 * Retrieves JWT payload data from the store based on the active request index.
 * If valid JWT data is found, it updates the `jwtPayloads` object with the
 * retrieved values for `tokenAddToField`, `algorithm`, `secret`, and `payload`.
 * If no valid data is found, it sets default values for the JWT properties.
 */
const retrieveValuesFromStore = () => {
  const jwtPayloadDataFromStore = apiTabStore.getRequestFieldData(
    props.activeRequestIndex,
    RequestSection.AUTHORIZATION,
    AUTHORIZATION_AUTH_TYPES.JWT_BEARER
  )
  if (
    jwtPayloadDataFromStore &&
    typeof jwtPayloadDataFromStore === 'object' &&
    'tokenAddToField' in jwtPayloadDataFromStore &&
    'algorithm' in jwtPayloadDataFromStore &&
    'secret' in jwtPayloadDataFromStore &&
    'payload' in jwtPayloadDataFromStore
  ) {
    jwtPayloads.value.jwtAddToRequest = jwtPayloadDataFromStore.tokenAddToField || ''
    jwtPayloads.value.jwtAlgorithm = jwtPayloadDataFromStore.algorithm || ''
    jwtPayloads.value.jwtSecret = jwtPayloadDataFromStore.secret || ''
    jwtPayloads.value.jwtPayload = jwtPayloadDataFromStore.payload || ''
  } else {
    jwtPayloads.value.jwtAddToRequest = JWT_TOKEN_ADD_TO.REQUEST_HEADERS
    jwtPayloads.value.jwtAlgorithm = JWT_AVAILABLE_ALGORITHMS.HS256
    jwtPayloads.value.jwtSecret = ''
    jwtPayloads.value.jwtPayload = ''
  }
}

onMounted(() => {
  retrieveValuesFromStore()
})

watch(
  () => props.activeRequestIndex,
  () => {
    retrieveValuesFromStore()
  }
)

watch(
  () => jwtPayloads.value,
  () => {
    apiTabStore.updateRequestField(
      props.activeRequestIndex,
      RequestSection.AUTHORIZATION,
      jwtPayloads.value,
      AUTHORIZATION_AUTH_TYPES.JWT_BEARER
    )
  },
  { deep: true }
)
</script>

<template>
  <span class="text-mid text-xs font-extralight tracking-tight">
    The authorization header will be automatically generated when you send the request.
    <a href="https://www.jdoodle.com/docs/ide/api-testing" target="_blank" class="underline"
      >Learn more about authorization.</a
    >
  </span>
  <div class="flex h-full w-full flex-col gap-10">
    <div class="flex h-10 w-full flex-row items-start justify-center">
      <div class="text-high flex h-full w-1/2 items-center text-xs font-medium">
        Add JWT token to:
      </div>
      <div class="h-full w-1/2">
        <BaseSelect
          :list="jwtTokenAddToFields"
          :valueType="FORM_SELECT_VALUE_TYPE.NAME"
          v-model="jwtPayloads.jwtAddToRequest"
          customClass="text-high block h-9 w-full appearance-none rounded-md background-primary tracking-tight !p-2 outline-none !border-border-primary"
          :showLable="false"
          :getSelectedByItem="true"
          :isLightGray="false"
        />
      </div>
    </div>

    <div class="flex h-10 w-full flex-col items-start justify-center gap-2">
      <div class="flex h-full w-full items-center text-base font-medium">Algorithm</div>
      <div class="h-full w-full">
        <BaseSelect
          :list="jwtAvailableAlgorithms"
          :valueType="FORM_SELECT_VALUE_TYPE.NAME"
          v-model="jwtPayloads.jwtAlgorithm"
          customClass="text-high block h-9 w-full appearance-none rounded-md background-primary tracking-tight !p-2 outline-none !border-border-primary"
          :showLable="false"
          :getSelectedByItem="true"
          :isLightGray="false"
        />
      </div>
    </div>

    <div class="flex h-full w-full flex-col">
      <BaseInput
        id="jwt-secret"
        label="Secret"
        :inputType="'text'"
        v-model="jwtPayloads.jwtSecret"
        :isBoldLabel="true"
        labelClass="text-xs font-medium text-high"
        :isLightGray="false"
        isCustomClass="border !border-border-primary background-primary outline-none"
      />
      <BaseTextbox
        label="Payload"
        labelClass="text-xs font-medium text-high"
        :isBoldLabel="true"
        v-model="jwtPayloads.jwtPayload"
        :isLightGray="false"
        isCustomClass="border !border-border-primary background-primary outline-none"
      />
    </div>
  </div>
</template>
